import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const client = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000
});

// Add auth token to requests
client.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle errors
client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    throw error;
  }
);

// Auth API
export const auth = {
  register: (email, password, role, firstName, lastName) =>
    client.post('/auth/register', { email, password, role, first_name: firstName, last_name: lastName }),
  login: (email, password) =>
    client.post('/auth/login', { email, password }),
  me: () =>
    client.get('/auth/me'),
  logout: () =>
    client.post('/auth/logout')
};

// Shifts API
export const shifts = {
  list: (status, employerId) =>
    client.get('/shifts', { params: { status, employer_id: employerId } }),
  get: (id) =>
    client.get(`/shifts/${id}`),
  create: (data) =>
    client.post('/shifts', data),
  update: (id, data) =>
    client.put(`/shifts/${id}`, data),
  delete: (id) =>
    client.delete(`/shifts/${id}`)
};

// Applications API
export const applications = {
  list: () =>
    client.get('/applications'),
  apply: (shiftId) =>
    client.post('/applications', { shift_id: shiftId }),
  hire: (id) =>
    client.put(`/applications/${id}/hire`),
  decline: (id) =>
    client.put(`/applications/${id}/decline`)
};

// Hires API
export const hires = {
  list: (status) =>
    client.get('/hires', { params: { status } }),
  get: (id) =>
    client.get(`/hires/${id}`),
  confirm: (id) =>
    client.put(`/hires/${id}/confirm`),
  complete: (id) =>
    client.post(`/hires/${id}/complete`),
  dispute: (id, reason) =>
    client.post(`/hires/${id}/dispute`, { reason })
};

// Documents API
export const documents = {
  list: () =>
    client.get('/documents'),
  upload: (documentType, fileUrl, expiresAt) =>
    client.post('/documents/upload', { document_type: documentType, file_url: fileUrl, expires_at: expiresAt }),
  get: (id) =>
    client.get(`/documents/${id}`),
  verify: (id, approved, rejectionReason) =>
    client.put(`/documents/${id}/verify`, { approved, rejection_reason: rejectionReason })
};

// Messages API
export const messages = {
  getForShift: (shiftId) =>
    client.get(`/messages/${shiftId}`),
  send: (shiftId, recipientId, content) =>
    client.post('/messages', { shift_id: shiftId, recipient_id: recipientId, content }),
  getConversations: (userId) =>
    client.get(`/messages/conversations/${userId}`)
};

// Ratings API
export const ratings = {
  create: (hireId, rateeId, rating, reviewText) =>
    client.post('/ratings', { hire_id: hireId, ratee_id: rateeId, rating, review_text: reviewText }),
  getForUser: (userId) =>
    client.get(`/ratings/${userId}`)
};

// Admin API
export const admin = {
  stats: () =>
    client.get('/admin/stats'),
  getDisputes: () =>
    client.get('/admin/disputes'),
  resolveDispute: (id, resolution, releasePayment) =>
    client.put(`/admin/disputes/${id}/resolve`, { resolution, release_payment: releasePayment }),
  getPendingDocuments: () =>
    client.get('/admin/documents/pending')
};

export default client;
